import { useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import SideNavigation from '@/components/layout/SideNavigation';
import FloatingActionMenu from '@/components/layout/FloatingActionMenu';
import VideoFinderModal from '@/components/video-finder/VideoFinderModal';
import { VideoFinderProvider } from '@/contexts/VideoFinderContext';
import { isImpersonating } from '@/services/impersonation';
import { useUser } from '@/contexts/UserContext';
import { isEmptyFeatureConfig } from '@/models/feature-config';

interface PersistentLayoutProps {
  onLogout: () => void;
}

const NO_FEATURES_ALLOWED_PATHS = ['/pricing', '/user-settings'];

export default function PersistentLayout({ onLogout }: PersistentLayoutProps) {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    return saved ? JSON.parse(saved) : false;
  });

  const handleSidebarCollapsedChange = (collapsed: boolean) => {
    setIsSidebarCollapsed(collapsed);
    localStorage.setItem('sidebarCollapsed', JSON.stringify(collapsed));
  };

  const impersonating = isImpersonating();

  const { user, isStaff, loading } = useUser();
  const location = useLocation();

  if (
    !loading &&
    user !== null &&
    !isStaff &&
    isEmptyFeatureConfig(user.featureConfig) &&
    !NO_FEATURES_ALLOWED_PATHS.includes(location.pathname)
  ) {
    return <Navigate to="/pricing" replace />;
  }

  return (
    <VideoFinderProvider>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800 flex flex-col">
        <SideNavigation
          onLogout={onLogout}
          onCollapsedChange={handleSidebarCollapsedChange}
        />

        <main className={`px-2 sm:px-6 lg:px-8 transition-all duration-300 ease-in-out flex-grow overflow-hidden ${
          isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'
        } ${impersonating ? 'pt-[92px] lg:pt-[60px]' : 'pt-16 lg:pt-8'}`}>
        {/*
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl p-8 min-h-[calc(100vh-8rem)]">
        */}
          <div className="p-4 lg:p-8">
            <Outlet />
          </div>
        </main>

        <FloatingActionMenu />
        <VideoFinderModal />
      </div>
    </VideoFinderProvider>
  );
}
